import React from 'react';
import Navbar from '../../components/Navbar';
import { 
  ArrowPathIcon, 
  CloudArrowDownIcon, 
  ClockIcon, 
  MagnifyingGlassIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  LightBulbIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const SyncMethodsPage: React.FC = () => {
  const syncMethods = [
    {
      name: 'Initial Organization Connection',
      icon: <CloudArrowDownIcon className="h-8 w-8" />,
      type: 'Automatic',
      color: 'blue',
      description: 'Comprehensive first-time sync when connecting a new Salesforce org',
      details: [
        'Downloads all flows with complete metadata',
        'Extracts every element, connector, and variable',
        'Tracks all object and field references',
        'Automatically generates AI summaries for all flows',
        'Sets up the foundation for future syncs'
      ],
      when: 'Runs automatically when you connect a new Salesforce organization',
      duration: 'Can take several minutes for large orgs',
      recommendation: 'Let it complete fully for best results'
    },
    {
      name: 'Nightly Automated Sync',
      icon: <ClockIcon className="h-8 w-8" />,
      type: 'Automatic',
      color: 'indigo',
      description: 'Smart sync that runs automatically every night to keep your flows updated',
      details: [
        'Only syncs flows that have changed',
        'Compares ActiveVersionId to detect new versions',
        'Also checks timestamps for modifications',
        'Performs full processing on changed flows',
        'Generates AI summaries for updated flows',
        'Updates all connected organizations'
      ],
      when: 'Runs daily at 2 AM UTC (automatically)',
      duration: 'Usually completes within minutes',
      recommendation: 'The most efficient way to stay up-to-date'
    },
    {
      name: 'Check for New or Updated Flows',
      icon: <MagnifyingGlassIcon className="h-8 w-8" />,
      type: 'Manual',
      color: 'green',
      description: 'Smart check that shows you what has changed before syncing',
      details: [
        'Compares ActiveVersionId to detect changes',
        'Distinguishes new flows from updated versions',
        'Shows deleted/deactivated flows to remove',
        'Lets you confirm before syncing',
        'Performs comprehensive sync of changed flows only',
        'Generates AI summaries for updated flows'
      ],
      when: 'Use when you want to see what\'s changed before syncing',
      duration: 'Check is instant, sync depends on number of changes',
      recommendation: 'Best for targeted updates when you know flows have changed'
    },
    {
      name: 'Refresh Single Flow',
      icon: <DocumentTextIcon className="h-8 w-8" />,
      type: 'Manual',
      color: 'purple',
      description: 'Comprehensive sync of one specific flow',
      details: [
        'Fully processes the selected flow',
        'Updates all elements and connectors',
        'Extracts variables and properties',
        'Regenerates AI summary',
        'Shows real-time progress'
      ],
      when: 'Use when working on a specific flow that needs immediate update',
      duration: 'Usually completes within 30 seconds',
      recommendation: 'Perfect for testing changes to individual flows'
    }
  ];

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
      indigo: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
      green: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
      yellow: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
      purple: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Understanding Sync Methods</h1>
          <p className="mt-2 text-lg text-gray-600">
            FlowDocs uses different sync methods to keep your Salesforce flows up-to-date. 
            Each method serves a specific purpose and has different capabilities.
          </p>
        </div>

        {/* Quick Tips */}
        <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start">
            <LightBulbIcon className="h-6 w-6 text-blue-600 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-lg font-medium text-blue-900">Quick Tips</h3>
              <ul className="mt-2 space-y-1 text-blue-800">
                <li>• The nightly sync handles most updates automatically - you usually don't need to sync manually</li>
                <li>• Use "Check for New or Updated Flows" when you've made changes in Salesforce and need them immediately</li>
                <li>• Single flow refresh is perfect when working on a specific flow</li>
                <li>• Avoid frequent manual syncs to conserve API calls</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Sync Methods Grid */}
        <div className="space-y-6">
          {syncMethods.map((method) => {
            const colors = getColorClasses(method.color);
            return (
              <div key={method.name} className={`${colors.bg} ${colors.border} border rounded-lg p-6`}>
                <div className="flex items-start">
                  <div className={`${colors.text} flex-shrink-0`}>
                    {method.icon}
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-semibold text-gray-900">{method.name}</h3>
                      <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                        method.type === 'Automatic' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {method.type}
                      </span>
                    </div>
                    
                    <p className="mt-2 text-gray-700">{method.description}</p>
                    
                    <div className="mt-4 grid md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">What it does:</h4>
                        <ul className="space-y-1">
                          {method.details.map((detail, index) => (
                            <li key={index} className="flex items-start">
                              <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <span className="ml-2 text-sm text-gray-600">{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <h4 className="font-medium text-gray-900 flex items-center">
                            <InformationCircleIcon className="h-5 w-5 mr-1" />
                            When to use
                          </h4>
                          <p className="mt-1 text-sm text-gray-600">{method.when}</p>
                        </div>
                        
                        <div>
                          <h4 className="font-medium text-gray-900">Duration</h4>
                          <p className="text-sm text-gray-600">{method.duration}</p>
                        </div>
                        
                        <div className={`p-3 rounded ${colors.bg} ${colors.border} border`}>
                          <p className={`text-sm font-medium ${colors.text}`}>
                            💡 {method.recommendation}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Comparison Table */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Comparison</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg overflow-hidden shadow">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Feature
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Initial Connection
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Nightly Sync
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Check for Updates
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Refresh Single
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Scope
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500">All flows</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500">Changed only</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500">Changed only</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500">One flow</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Processing Depth
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 mx-auto" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 mx-auto" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    AI Summaries
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 mx-auto" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 mx-auto" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 mx-auto" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 mx-auto" />
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Handles Deletions
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500">N/A</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 mx-auto" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500">N/A</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    API Efficiency
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500">Low</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500">High</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500">High</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500">High</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Best Practices */}
        <div className="mt-12 bg-gray-50 rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Best Practices</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-green-700 mb-2 flex items-center">
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                Recommended
              </h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Let the nightly sync handle regular updates</li>
                <li>• Use "Check for New or Updated Flows" when you need immediate changes</li>
                <li>• Use single-flow refresh when working on specific flows</li>
                <li>• Monitor sync status in organization settings</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-red-700 mb-2 flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                Avoid
              </h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Frequent manual syncs (wastes API calls)</li>
                <li>• Syncing all flows when only some have changed</li>
                <li>• Syncing during Salesforce maintenance windows</li>
                <li>• Multiple syncs in quick succession</li>
              </ul>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-900 mb-2">
                How often should I manually sync?
              </h3>
              <p className="text-gray-600">
                In most cases, you don't need to sync manually at all. The nightly sync keeps everything 
                up-to-date automatically. Only sync manually when you need immediate updates or are 
                working on specific flows.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-900 mb-2">
                How does "Check for New or Updated Flows" work?
              </h3>
              <p className="text-gray-600">
                This feature queries Salesforce for all flows with active versions and compares them with our database. 
                Since Salesforce only returns flows that have an ActiveVersion, we can detect:
              </p>
              <ul className="mt-2 ml-4 list-disc text-gray-600">
                <li><strong>New flows:</strong> DeveloperName found in Salesforce but not in our database</li>
                <li><strong>Updated flows:</strong> Same DeveloperName but different ActiveVersionId</li>
                <li><strong>Deleted/Deactivated flows:</strong> DeveloperName exists in our database but NOT returned by Salesforce (because it no longer has an active version)</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-900 mb-2">
                What happens if a sync fails?
              </h3>
              <p className="text-gray-600">
                Failed syncs are logged and can be retried. The nightly sync will automatically attempt 
                to sync any flows that previously failed. You can also manually trigger a sync for 
                specific flows if needed.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SyncMethodsPage;